import { Grid } from '@material-ui/core';
import { Dispatch, VFC } from 'react';
import { useForm, UseFormReturn, FormProvider } from 'react-hook-form';
import { useHooks } from './useHooks';
import { useStyles } from './useStyles';
import { SiteProcessTextfield } from '../../input/ProcessTextfield';
import { SiteProcessSelectfield } from '../../input/ProcessSelectfield';
import { SiteProcessDatePicker } from '../../input/ProcessDatePicker';
import { validationMessage } from '../../../../const/ProcessConst';
import { TEditSiteTaskForm } from '../../../../types/task';
import { SITE_TASK_FORM_ORDER_TYPE_LIST } from '../../../../const/TaskConst';
import { BaseModal } from '../../../molecules/BaseModal';
import { SiteTaskDeleteModal } from '../TaskDeleteModal';
import { SiteProcessRadioGroup } from '../../input/ProcessRadioGroup';
import HelpIcon from '@material-ui/icons/Help';
import { CustomTooltip } from '../../../tooltip/CustomTooltip';
import { tooltipContents } from '../../../tooltip/Const';
import { TSelectItem } from '../../../../types/common';

export type Props = {
  isOpen: boolean;
  setIsOpen: Dispatch<boolean>;
  afterUpdate: () => void;
  constructId: number;
  siteTaskId: number;
  afterDelete: () => void; // 削除モーダル用
};

export const SiteTaskEditModal: VFC<Props> = props => {
  const {
    isOpen,
    setIsOpen,
    afterUpdate,
    constructId,
    siteTaskId,
    afterDelete
  } = props;
  const classes = useStyles();
  const methods: UseFormReturn<TEditSiteTaskForm> = useForm();
  const {
    setValue,
    getValues,
    handleSubmit,
    reset,
    formState: { errors }
  } = methods;

  const {
    cancelSubmit,
    submit,
    handleChangeOrderTypeRadio,
    siteProcessList,
    purchaseOrderList,
    employeeList,
    statusList,
    handleDeleteSubmit,
    isOpenSiteTaskDeleteModal,
    setIsOpenSiteTaskDeleteModal,
    handleAfterDelete,
    siteTaskData
  } = useHooks({
    setIsOpen,
    afterUpdate,
    afterDelete,
    constructId,
    siteTaskId,
    getValues,
    setValue,
    reset,
    isOpen
  });

  // タスクの担当者（担当者プルダウン用）
  const staffData: TSelectItem | null = siteTaskData?.charge_user
    ? {
        label: siteTaskData.charge_user.name,
        value: siteTaskData.charge_user.id
      }
    : null;

  return (
    <BaseModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      handleSubmit={handleSubmit(submit, (erros: any) => {})}
      handleCancelSubmit={cancelSubmit}
      title={'タスク編集'}
      buttonCaption={'更新する'}
      cancelButtonCaption={'キャンセル'}
      subDeleteButton={true}
      handleDeleteSubmit={handleDeleteSubmit}
    >
      <FormProvider {...methods}>
        {/* タスク */}
        <Grid item xs={10} md={3} className={classes.gridItem}>
          <label className={classes.label}>タスク*</label>
        </Grid>
        <Grid item xs={10} md={7}>
          <SiteProcessTextfield
            name='site_task_name'
            className={`${classes.input} ${classes.gridItem}`}
            required={true}
            maxLength={100}
            errorMessage={errors?.site_task_name?.message}
          />
        </Grid>
        {/* /タスク */}
        {/* 担当者 */}
        <Grid item xs={10} md={3} className={classes.gridItem}>
          <label className={classes.label}>担当者</label>
        </Grid>
        <Grid item xs={10} md={7}>
          <SiteProcessSelectfield
            name='charge_user_id'
            listDatas={employeeList}
            className={`${classes.input} ${classes.gridItem}`}
            required={false}
            staffData={staffData}
          />
        </Grid>
        {/* /担当者 */}
        {/* 工程 */}
        <Grid container alignItems='center' justify='center'>
          <Grid item xs={10} md={3} className={classes.gridItem}>
            <label className={classes.label}>工程*</label>
          </Grid>
          <Grid item xs={10} md={7}>
            <SiteProcessSelectfield
              name='site_process_id'
              listDatas={siteProcessList}
              className={`${classes.input} ${classes.gridItem}`}
              required={true}
              error={'site_process_id' in errors}
              errorMessage={
                errors?.site_process_id && validationMessage.required
              }
            />
          </Grid>
        </Grid>
        {/* /工程 */}
        {/* 期限 */}
        <Grid item xs={10} md={3} className={classes.gridItem}>
          <label className={classes.label}>期限</label>
        </Grid>
        <Grid item xs={10} md={7}>
          <div className={classes.leftAlignContainer}>
            <div className={classes.itemMargin}>
              <SiteProcessDatePicker
                name='planned_end_date'
                label='期限'
                required={false}
                fullWidth={true}
                className={classes.gridItem}
              />
            </div>
          </div>
        </Grid>
        {/* /期限 */}
        {/* 内容 */}
        <Grid item xs={10} md={3} className={classes.gridItem}>
          <label className={classes.label}>内容</label>
        </Grid>
        <Grid item xs={10} md={7}>
          <SiteProcessTextfield
            name='detail'
            fullWidth={true}
            multiline={true}
            style={{ padding: 0 }}
            className={`${classes.input} ${classes.gridItem}`}
            maxLength={500}
            errorMessage={errors?.detail?.message}
            inputProps={{ minHeight: 60, maxHeight: 100 }}
            inputElementProps={{ style: { padding: '2px 14px' } }}
          />
        </Grid>
        {/* /内容 */}
        {/* ステータス */}
        <Grid item xs={10} md={3} className={classes.gridItem}>
          <label className={classes.label}>ステータス*</label>
        </Grid>
        <Grid item xs={10} md={7}>
          <SiteProcessSelectfield
            name='status'
            listDatas={statusList}
            className={`${classes.input} ${classes.gridItem}`}
            required={true}
            error={'status' in errors}
            errorMessage={errors?.status && validationMessage.required}
          />
        </Grid>
        {/* /ステータス */}
        {/* 発注書区分 */}
        <Grid item xs={10} md={3} className={classes.gridItem}>
          <label className={classes.label}>発注書区分</label>
        </Grid>
        <Grid item xs={10} md={7}>
          <div
            className={`${classes.leftAlignContainer} ${classes.input} ${classes.gridItem} ${classes.flexWrap}`}
          >
            <SiteProcessRadioGroup
              name='order_type'
              onChange={handleChangeOrderTypeRadio}
              listDatas={SITE_TASK_FORM_ORDER_TYPE_LIST}
              getValues={getValues}
            />
          </div>
        </Grid>
        {/* /発注書区分 */}
        {/* 発注書 */}
        <Grid item xs={10} md={3} className={classes.gridItem}>
          <label className={classes.label}>発注書</label>
          <CustomTooltip
            title={tooltipContents.TaskForOrder}
            placement='bottom-start'
          >
            <HelpIcon color='action' style={{ marginLeft: 8 }} />
          </CustomTooltip>
        </Grid>
        <Grid item xs={10} md={7}>
          <SiteProcessSelectfield
            name='order_id'
            listDatas={purchaseOrderList}
            className={`${classes.input} ${classes.gridItem}`}
            required={false}
            error={false}
            errorMessage={''}
          />
        </Grid>
        {/* /発注書 */}
        {/* 完了日 */}
        <Grid item xs={10} md={3} className={classes.gridItem}>
          <label className={classes.label}>完了日</label>
        </Grid>
        <Grid item xs={10} md={7}>
          <div className={classes.leftAlignContainer}>
            <div className={classes.itemMargin}>
              <SiteProcessDatePicker
                name='actual_end_date'
                label='完了日'
                required={false}
                fullWidth={true}
                className={classes.gridItem}
              />
            </div>
          </div>
        </Grid>
        {/* /完了日 */}
      </FormProvider>
      {/* タスク削除モーダル */}
      <SiteTaskDeleteModal
        isOpen={isOpenSiteTaskDeleteModal}
        setIsOpen={setIsOpenSiteTaskDeleteModal}
        afterSubmit={handleAfterDelete}
        siteTaskId={siteTaskId}
      />
    </BaseModal>
  );
};
